import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DataTable, ColumnDef } from "../common/DataTable";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { AlertCircle, MessageSquare } from "lucide-react";
import { MatchedClient } from "@/types/staffing/matchedClient";
import { getMatchedClientsForStaff, getMatchedClientsForApplicant, createProcess } from "@/services/staffing/Staffing";
import { openStaffingGroupByReferenceId } from "@/utils/staffingUtil";
import { useApiClient } from "@/context/ApiContext";
import PercentBar from "../common/PercentBar";
import ClientStaffingContextMenu from "./contextMenus/ClientStaffingContextMenu";
import { CurrentUserContext } from "@/context/CurrentUserContext";

interface ClientMatchesListProps {
    identifier: string;
    isApplicant: boolean;
    region: string | null;
    widget: boolean;
    active?: boolean;
    staffFullName?: string | null;
    staffRole?: string | null;
    onAddSuccess?: (affectedPostingIds: Set<number>) => void;
}

const sharedColumns: ColumnDef<MatchedClient>[] = [
    { key: 'percentMatch', header: 'Overall %', sortable: true, render: (row) => <PercentBar percent={row.percentMatch} /> },
    { key: 'percentMatchAvail', header: '% Match Availability', sortable: true, render: (row) => <PercentBar percent={row.percentMatchAvail} /> },
    { key: 'percentMatchDistance', header: '% Match Distance', sortable: true, render: (row) => <PercentBar percent={row.percentMatchDistance} /> },
    { key: 'percentMatchCredentials', header: '% Match Credentials', sortable: true, render: (row) => <PercentBar percent={row.percentMatchCredentials} /> },
    { key: 'age', header: 'Age', sortable: true },
    { key: 'gender', header: 'Gender', sortable: true },
    { key: 'clientStatus', header: 'Status', sortable: true },
    { key: 'parentCaregiver1City', header: 'City', sortable: true },
    { key: 'insuranceCompany', header: 'Funding Source', sortable: true },
    { key: 'distance', header: 'Distance (mi)', sortable: true, render: (row) => row.distance.toFixed(1) },
];

const ClientMatchesList = ({ identifier, isApplicant, region, widget = false, active = true, staffFullName, staffRole, onAddSuccess }: ClientMatchesListProps) => {
    const apiClient = useApiClient();
    const {originalUser} = useContext(CurrentUserContext)
    const [clients, setClients] = useState<MatchedClient[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedClientIds, setSelectedClientIds] = useState<Set<number>>(new Set());
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<{ title: string; detail: string } | null>(null);

    const fetchClients = useCallback(() => {
        if (!region) return;
        setLoading(true);
        const fetcher = isApplicant
            ? getMatchedClientsForApplicant(identifier, region, 20, apiClient)
            : getMatchedClientsForStaff('positive\\' + identifier, region, 20, apiClient);
        fetcher
            .then(res => setClients(res.data ?? []))
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    }, [region, isApplicant, identifier, apiClient]);

    useEffect(() => {
        if (!active) return;
        fetchClients();
    }, [active, fetchClients]);
    const computeSaveText = (): string => {
        if (submitting) return 'Adding';
        if (widget) { 
            return `Express interest in ${selectedClientIds.size} Posting${selectedClientIds.size !== 1 ? 's' : ''}`
        }
        else {
            return `Add to ${selectedClientIds.size} Posting${selectedClientIds.size !== 1 ? 's' : ''}`
        }
    }
    const addToProcess = async () => {
        if (selectedClientIds.size === 0) return;
        setSubmitting(true);
        setError(null);
        try {
            const selected = clients.filter(c => selectedClientIds.has(c.clientID) && c.postingID != null);
            const staff = isApplicant ? identifier : `positive\\${identifier}`;
            let lastReferenceId: string | null = null;
            for (const client of selected) {
                const res = await createProcess({
                    postingId: client.postingID!,
                    staff,
                    source: widget ? 'Openings' : 'Staffing Dashboard',
                    applicant: isApplicant,
                    author: originalUser.LoginName,
                    adHocGroup: {
                        clientId: client.clientID,
                        clientName: client.clientName,
                        staffFullName: staffFullName ?? '',
                        staffRole: staffRole ?? '',
                        staffRegion: region ?? '',
                    }
                }, apiClient);
                lastReferenceId = res.data?.referenceId ?? lastReferenceId;
            }
            setSelectedClientIds(new Set());
            fetchClients();
            const affectedIds = new Set(selected.map(c => c.postingID!));
            onAddSuccess?.(affectedIds);
            if (lastReferenceId) openStaffingGroupByReferenceId(lastReferenceId);
        } catch (err: any) {
            if (err?.response?.status === 409) {
                const data = err.response.data;
                setError({ title: data?.title ?? 'Conflict', detail: data?.detail ?? 'A duplicate process entry already exists.' });
            } else {
                console.error(err);
            }
        } finally {
            setSubmitting(false);
        }
    };

    /**
     * A match the staff member is already in process on has a staffing group, so
     * the Openings widget offers chat on that row instead of the express interest
     * checkbox. Same reference id StaffContextMenu builds: process-login-client.
     */
    const openChat = useCallback((row: MatchedClient) => {
        if (row.processRowID == null) return;
        const staffLoginName = identifier.includes('\\') ? identifier.split('\\')[1] : identifier;
        openStaffingGroupByReferenceId(`${row.processRowID}-${staffLoginName}-${row.clientID}`);
    }, [identifier]);

    const columns = useMemo<ColumnDef<MatchedClient>[]>(() => [
        {
            key: 'select',
            header: '',
            sticky: true,
            render: (row) => (
                // Fixed width box so the column doesn't resize between the two
                // controls, and the checkbox stays centred under the chat button.
                <div className="flex w-8 items-center justify-center">
                    {widget && row.processRowID != null ? (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-primary"
                            aria-label="Open staffing chat"
                            title="Staffing Chat"
                            onClick={() => openChat(row)}
                        >
                            <MessageSquare className="h-4 w-4" />
                        </Button>
                    ) : (
                        <Checkbox
                            checked={selectedClientIds.has(row.clientID)}
                            disabled={submitting || row.postingID == null}
                            onCheckedChange={(checked) => {
                                setSelectedClientIds(prev => {
                                    const next = new Set(prev);
                                    checked ? next.add(row.clientID) : next.delete(row.clientID);
                                    return next;
                                });
                            }}
                        />
                    )}
                </div>
            )
        },
        {
            key: 'clientName', header: 'Client', sortable: true, sticky: true,
            render: (row) => <ClientStaffingContextMenu clientId={row.clientID} clientName={row.clientName} isPosting={true} isMatch={true} isWidget={widget}/>
        },
        ...sharedColumns,
    ], [selectedClientIds, submitting, widget, openChat]);

    return (
        <div className="flex flex-col gap-2">
            {error && (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>{error.title}</AlertTitle>
                    <AlertDescription>{error.detail}</AlertDescription>
                </Alert>
            )}
            <DataTable data={clients} columns={columns} loading={loading} />
            <div className="flex justify-end">
                <Button
                    size="sm"
                    disabled={selectedClientIds.size === 0 || submitting}
                    onClick={addToProcess}
                >
                    {computeSaveText()}
                </Button>
            </div>
        </div>
    );
};

export default ClientMatchesList;
